import { Injectable } from '@angular/core';
import { API_ROUTES } from '@app/core/constants/api-routes';
import { ResponseModel } from '@app/core/models/api.response.model';
import { SelectListModel } from '@app/core/models/select.list.model';
import { ApiClientService } from '@app/core/services/api-client.service';
import { AppConfigService } from '@app/core/services/app-config.service';
import { map, Observable } from 'rxjs';
import { String } from 'typescript-string-operations';
import { UserModel, UserScoreCardModel, UserSelectListModel, UserTeamModel } from './user.model';
import { Endpoint } from '@app/core/constants/enums';
import { LoginHelper } from '@app/core/helpers/login.helper';
import { GridFilterRequestModel } from '@app/core/models/list-filter.model';
import { UpdateUserNotificationRequestModel, UserNotificationModel } from '@app/core/models/user-notification.model';
import { LazyList } from '@app/core/models/lazy-list.response.model';
import { Constants } from '@app/core/constants/constants';

@Injectable()
export class UserService {

    identityEndpoint: string;

    constructor(
        private apiClient: ApiClientService,
        private readonly appConfigService: AppConfigService
    ) {
        this.identityEndpoint = this.appConfigService.getEndpoint(Endpoint.Identity);
    }

    getUsers(): Observable<ResponseModel<UserModel[]>> {
        const url = `${this.appConfigService.getEndpoint(Endpoint.Identity)}${API_ROUTES.identity.user.getUserList}`;
        return this.apiClient.get<ResponseModel<UserModel[]>>(url);
    }

    createUser(payload: UserModel): Observable<ResponseModel<UserModel>> {
        const url = `${this.appConfigService.getEndpoint(Endpoint.Identity)}${API_ROUTES.identity.user.createUser}`;
        return this.apiClient.post<UserModel, ResponseModel<UserModel>>(url, payload);
    }

    updateUser(payload: UserModel): Observable<ResponseModel<UserModel>> {
        const url = `${this.appConfigService.getEndpoint(Endpoint.Identity)}${API_ROUTES.identity.user.updateUser}`;
        return this.apiClient.put<UserModel, ResponseModel<UserModel>>(url, payload);
    }

    updateUserStatus(isActive: boolean, userId: number): Observable<ResponseModel<UserModel>> {
        let url = `${this.appConfigService.getEndpoint(Endpoint.Identity)}${API_ROUTES.identity.user.updateUserStatus}`;
        url = String.format(url, isActive, userId, LoginHelper.GetUserDetail().userId);
        return this.apiClient.put<undefined, ResponseModel<UserModel>>(url);
    }

    getRegionSelectList(regionId?: string): Observable<ResponseModel<SelectListModel[]>> {
        let url = `${this.appConfigService.getEndpoint(Endpoint.Administration)}${API_ROUTES.administration.region.getRegionSelectList}`;
        url = url + (regionId ? `?regionId=${regionId}` : ``);
        return this.apiClient.get<ResponseModel<SelectListModel[]>>(url);
    }
    getUserByDepartmentCodeTypeAhead(departmentCode: string, userName: string): Observable<ResponseModel<SelectListModel[]>> {
        let url = `${this.appConfigService.getEndpoint(Endpoint.Identity)}${API_ROUTES.identity.user.getUserByDepartmentCodeTypeAhead}`;
        url = String.format(url, departmentCode, userName);
        return this.apiClient.get<ResponseModel<SelectListModel[]>>(url);
    }

    getSalesScoreCard(userId: number): Observable<ResponseModel<UserScoreCardModel>> {
        if (userId) {
            let url = `${this.appConfigService.getEndpoint(Endpoint.Identity)}${API_ROUTES.identity.userScoreCard.getSalesScoreCard}`;
            url = String.format(url, userId);
            return this.apiClient.get<ResponseModel<UserScoreCardModel>>(url);
        }
        else {
            userId = -1;
            let url = `${this.appConfigService.getEndpoint(Endpoint.Identity)}${API_ROUTES.identity.userScoreCard.getSalesScoreCard}`;
            url = String.format(url, userId);
            return this.apiClient.get<ResponseModel<UserScoreCardModel>>(url);
        }
    }

    getUnreadUserNotificationCount(): Observable<ResponseModel<number>> {
        let url = `${this.appConfigService.getEndpoint(Endpoint.Identity)}${API_ROUTES.identity.userNotification.getUnreadUserNotificationCount}`;
        url = String.format(url, LoginHelper.GetUserDetail().userId);
        return this.apiClient.get<ResponseModel<number>>(url);
    }

    getUserNotificationList(payload: GridFilterRequestModel): Observable<ResponseModel<LazyList<UserNotificationModel[]>>> {
        const url = `${this.appConfigService.getEndpoint(Endpoint.Identity)}${API_ROUTES.identity.userNotification.getUserNotificationList}`;
        return this.apiClient.post<GridFilterRequestModel, ResponseModel<LazyList<UserNotificationModel[]>>>(url, payload);
    }
    updateUserNotification(payload: UpdateUserNotificationRequestModel): Observable<ResponseModel<UserNotificationModel>> {
        const url = `${this.appConfigService.getEndpoint(Endpoint.Identity)}${API_ROUTES.identity.userNotification.updateUserNotification}`;
        return this.apiClient.post<UpdateUserNotificationRequestModel, ResponseModel<UserNotificationModel>>(url, payload);
    }

    /**
     * Get User Select List
     * @param userId When provided, the user is included even if inactive (e.g. a previously assigned but now deactivated user)
     * @returns Select List of Active Users
     */
    getUserSelectList(userId?: number): Observable<ResponseModel<UserSelectListModel[]>> {
        let url = `${this.identityEndpoint}${API_ROUTES.identity.user.getUserSelectList}`;
        url = url + (userId ? `?userId=${userId}` : '');
        return this.apiClient.get<ResponseModel<UserSelectListModel[]>>(url).pipe(
            map(response => {
                const selectList = response.data;
                selectList.unshift({ name: Constants.pleaseSelectDefaultText, userId: 0 });
                response.data = selectList;
                return response;
            })
        );
    }

    getUserProfileDetails(userId: number): Observable<ResponseModel<UserModel>> {
        let url = `${this.identityEndpoint}${API_ROUTES.identity.user.getUserProfileDetails}`;
        url = String.format(url, userId);
        return this.apiClient.get<ResponseModel<UserModel>>(url);
    }

    getProfilePicture(userId: number): Observable<ResponseModel<string>> {
        let url = `${this.identityEndpoint}${API_ROUTES.identity.user.getProfilePicture}`;
        url = String.format(url, userId);
        return this.apiClient.get<ResponseModel<string>>(url);
    }

    uploadProfilePicture(formData: FormData, userId: number): Observable<ResponseModel<string>> {
        let url = `${this.identityEndpoint}${API_ROUTES.identity.user.uploadProfilePicture}`;
        url = String.format(url, userId);
        return this.apiClient.post<FormData, ResponseModel<string>>(url, formData);
    }

    removeProfilePicture(userId: number): Observable<ResponseModel<boolean>> {
        let url = `${this.identityEndpoint}${API_ROUTES.identity.user.removeProfilePicture}`;
        url = String.format(url, userId);
        return this.apiClient.delete<ResponseModel<boolean>>(url);
    }

    getUserTeam(userId: number): Observable<ResponseModel<UserTeamModel>> {
        let url = `${this.identityEndpoint}${API_ROUTES.identity.user.getUserTeam}`;
        url = String.format(url, userId);
        return this.apiClient.get<ResponseModel<UserTeamModel>>(url);
    }
}
